import {
  appDefIds,
  getCurrentInstance,
  getModule,
  ModuleId,
  PageComponentId,
  isAppInstalled,
  buildBackToSiteQueryString,
} from '@wix/business-manager-api';
import { ModuleRegistry } from 'react-module-container';
import { INBOX_APP_DEF_ID } from '../constants/inboxConstants';
import { InboxModule } from '../module-registry/InboxModule';
import { uuid } from './uuid';
import { memoize as _memoize } from 'lodash';

export const isChatInstalled: () => boolean = () => {
  try {
    return isAppInstalled(appDefIds.chat);
  } catch (e) {
    console.warn('error', e);
    return false;
  }
};

export const isVirtualNumbersInstalled = () =>
  isAppInstalled(appDefIds.virtualNumbers);

export const getBusinessNumber = () =>
  isVirtualNumbersInstalled() &&
  !!window.ModuleRegistry.registeredMethods['virtual-numbers.getBusinessNumber']
    ? window.ModuleRegistry.invoke('virtual-numbers.getBusinessNumber')
    : undefined;

const canSendSMSTo_ = _memoize(
  (number: string): Promise<boolean> =>
    window.ModuleRegistry.invoke('virtualNumbers.canSendSMSToNumber', number),
);

export const canSendSMSTo = (number: string): Promise<boolean> =>
  window.ModuleRegistry.registeredMethods['virtualNumbers.canSendSMSToNumber']
    ? canSendSMSTo_(number)
    : Promise.resolve(true);

export const getInboxModule = () => getModule(ModuleId.Engage) as InboxModule;

export const getModuleParams = () => getInboxModule().moduleParams;

export const hasLiveSite = () => !!getModuleParams().liveSite;

export const getAppDefId = () => getInboxModule().appDefId;

export const getInstance = () => getCurrentInstance(getAppDefId());

export const getChatAppMarketLink = () =>
  `/editor/${
    getModuleParams().metaSiteId
  }?editorSessionId=${uuid()}&openpanel=market:appDefId:${appDefIds.chat}`;

export const getVirtualNumbersLink = () =>
  `/dashboard/${
    getModuleParams().metaSiteId
  }/virtual-numbers/settings?${buildBackToSiteQueryString()}`;

export const getChatSettingsUrl = () =>
  `${getModuleParams().liveSite.editUrl}&etpa=${appDefIds.chat}`;

export const showSettings = ({
  subRoute = '',
  queryParam = '',
  openInNewTab = false,
}: {
  subRoute?: string;
  queryParam?: string;
  openInNewTab?: boolean;
} = {}) => {
  const referrer = 'Inbox';
  if (openInNewTab) {
    // we can't use openInNewTab b/c of a BM bug (missing /dashboard in the url).
    // when they will fix it we can just use the below function
    const { metaSiteId } = getModuleParams();
    window.open(
      `/dashboard/${metaSiteId}/inbox/settings/${subRoute}?referralInfo=${referrer}&${queryParam}`,
    );
  } else {
    let route = subRoute;
    route += queryParam ? `?${queryParam}` : '';

    ModuleRegistry.invoke('businessManager.navigateTo', {
      pageComponentId: PageComponentId.InboxSettings,
      contextData: {
        appState: route ? route : null,
        appDefId: INBOX_APP_DEF_ID,
        referrer,
      },
      openInNewTab,
      viewId: PageComponentId.InboxSettings,
    });
  }
};

const INTERVAL_PERIOD = 100;
const CLEAR_INTERVAL_PERIOD = 10000;

export const getGlobalObj = async <T>(variableName: string): Promise<T> =>
  new Promise((res) => {
    const getter = () => window[variableName];
    const object = getter();
    if (object) {
      res(object);
    }

    const getObjInterval = setInterval(() => {
      const obj = getter();
      if (obj) {
        clearInterval(getObjInterval);
        return res(obj);
      }
    }, INTERVAL_PERIOD);

    setTimeout(() => {
      clearInterval(getObjInterval);
    }, CLEAR_INTERVAL_PERIOD);
  });
